import { Controller } from '@hotwired/stimulus'

// Минималистичная тёмная стилизация карты, согласованная с тёмной темой сайта.
const DARK_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#23252b' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#9aa0ab' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#23252b' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#2f323a' }]
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#7d828d' }]
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#1b1d22' }]
  }
]

/**
 * Рисует интерактивную Google-карту с клубами тренера.
 *
 * Координаты берутся из бэкенда (clubs.latitude / clubs.longitude) и
 * передаются через `data-coach-map-markers-value`. Маркеры — кастомные
 * HTML-оверлеи с логотипом клуба, поэтому Map ID не требуется.
 *
 * Скрипт Google Maps подключается на странице и оповещает контроллер
 * через событие `google-maps:ready`.
 */
export default class CoachMapController extends Controller {
  static targets = ['canvas']
  static values = {
    markers: Array,
    zoom: { type: Number, default: 14 }
  }

  #map = null
  #overlays = []
  #onMapsReady = null
  #themeObserver = null

  connect() {
    this.#onMapsReady = this.#init.bind(this)
    document.addEventListener('google-maps:ready', this.#onMapsReady)

    if (window.google?.maps) this.#init()
  }

  disconnect() {
    document.removeEventListener('google-maps:ready', this.#onMapsReady)
    this.#themeObserver?.disconnect()
    this.#themeObserver = null
    this.#overlays.forEach((overlay) => overlay.setMap(null))
    this.#overlays = []
    this.#map = null
  }

  zoomIn() {
    if (this.#map) this.#map.setZoom(this.#map.getZoom() + 1)
  }

  zoomOut() {
    if (this.#map) this.#map.setZoom(this.#map.getZoom() - 1)
  }

  #init() {
    if (this.#map || !window.google?.maps || !this.hasCanvasTarget) return

    const markers = this.markersValue.filter(
      (marker) => Number.isFinite(marker.lat) && Number.isFinite(marker.lng)
    )
    if (markers.length === 0) return

    this.#map = new google.maps.Map(this.canvasTarget, {
      center: { lat: markers[0].lat, lng: markers[0].lng },
      zoom: this.zoomValue,
      disableDefaultUI: true,
      clickableIcons: false,
      gestureHandling: 'cooperative',
      styles: this.#isDark() ? DARK_STYLES : []
    })

    const LogoOverlay = this.#buildOverlayClass()
    const bounds = new google.maps.LatLngBounds()

    markers.forEach((marker) => {
      const overlay = new LogoOverlay(
        new google.maps.LatLng(marker.lat, marker.lng),
        marker,
        this.#isDark()
      )
      overlay.setMap(this.#map)
      this.#overlays.push(overlay)
      bounds.extend({ lat: marker.lat, lng: marker.lng })
    })

    if (markers.length > 1) this.#map.fitBounds(bounds, 64)

    this.#watchTheme()
  }

  #isDark() {
    return document.documentElement.dataset.theme === 'dark'
  }

  #watchTheme() {
    this.#themeObserver = new MutationObserver(() => {
      if (!this.#map) return
      const dark = this.#isDark()
      this.#map.setOptions({ styles: dark ? DARK_STYLES : [] })
      this.#overlays.forEach((overlay) => overlay.applyTheme(dark))
    })
    this.#themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    })
  }

  #buildOverlayClass() {
    return class LogoOverlay extends google.maps.OverlayView {
      constructor(position, data, dark) {
        super()
        this.position = position
        this.data = data
        this.dark = dark
        this.element = null
      }

      onAdd() {
        const wrapper = this.data.href
          ? document.createElement('a')
          : document.createElement('div')
        if (this.data.href) {
          wrapper.href = this.data.href
          wrapper.setAttribute('aria-label', this.data.name || '')
        }
        Object.assign(wrapper.style, {
          position: 'absolute',
          transform: 'translate(-50%, -50%)',
          width: '48px',
          height: '48px',
          borderRadius: '16px',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
          boxShadow: '0 6px 16px rgba(0, 0, 0, 0.25)',
          cursor: this.data.href ? 'pointer' : 'default'
        })

        if (this.data.logo) {
          const img = document.createElement('img')
          img.src = this.data.logo
          img.alt = ''
          img.loading = 'lazy'
          Object.assign(img.style, {
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          })
          wrapper.appendChild(img)
        }

        this.element = wrapper
        this.applyTheme(this.dark)
        this.getPanes().overlayMouseTarget.appendChild(wrapper)
      }

      applyTheme(dark) {
        if (!this.element) return
        this.element.style.border = `4px solid ${
          dark ? 'rgba(240, 240, 240, 0.2)' : 'rgba(32, 33, 38, 0.15)'
        }`
        this.element.style.background = dark ? '#202126' : '#ffffff'
      }

      draw() {
        if (!this.element) return
        const point = this.getProjection().fromLatLngToDivPixel(this.position)
        if (!point) return
        this.element.style.left = `${point.x}px`
        this.element.style.top = `${point.y}px`
      }

      onRemove() {
        this.element?.remove()
        this.element = null
      }
    }
  }
}
